/** Procedural forest / wind bed via Web Audio — no asset files. */

export interface PlaceAudio {
  start: () => Promise<void>
  playCue: (cue: 'arrival' | 'discover' | 'stamp' | 'react' | 'type') => void
  setDucked: (ducked: boolean) => void
  setEnabled: (on: boolean) => void
  enabled: boolean
}

export function createPlaceAudio(): PlaceAudio {
  let ctx: AudioContext | null = null
  let master: GainNode | null = null
  let enabled = false
  let ducked = false

  const ensure = async () => {
    if (ctx) return
    ctx = new AudioContext()
    master = ctx.createGain()
    master.gain.value = 0.0001
    master.connect(ctx.destination)

    const bufferSize = 2 * ctx.sampleRate
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
    const data = noiseBuffer.getChannelData(0)
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1

    const makeNoise = (type: BiquadFilterType, freq: number, q: number, gain: number) => {
      const src = ctx!.createBufferSource()
      src.buffer = noiseBuffer
      src.loop = true
      const filter = ctx!.createBiquadFilter()
      filter.type = type
      filter.frequency.value = freq
      filter.Q.value = q
      const g = ctx!.createGain()
      g.gain.value = gain
      src.connect(filter)
      filter.connect(g)
      g.connect(master!)
      src.start()
    }

    makeNoise('lowpass', 420, 0.7, 0.22)
    makeNoise('bandpass', 1200, 0.8, 0.08)
    makeNoise('highpass', 2800, 0.5, 0.03)
  }

  const applyGain = () => {
    if (!ctx || !master) return
    const target = !enabled ? 0.0001 : ducked ? 0.04 : 0.16
    master.gain.cancelScheduledValues(ctx.currentTime)
    master.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.35)
  }

  const playCue = (cue: 'arrival' | 'discover' | 'stamp' | 'react' | 'type') => {
    if (!ctx || !master || !enabled) return
    // The reading blip fires many times a second, so it needs its own short,
    // quiet envelope rather than the melodic cue shape below.
    if (cue === 'type') {
      const now = ctx.currentTime
      const oscillator = ctx.createOscillator()
      const gain = ctx.createGain()
      oscillator.type = 'triangle'
      oscillator.frequency.value = 760 + Math.random() * 90
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.014, now + 0.006)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05)
      oscillator.connect(gain)
      gain.connect(master)
      oscillator.start(now)
      oscillator.stop(now + 0.06)
      return
    }
    const notes =
      cue === 'arrival'
        ? [392, 523.25, 659.25]
        : cue === 'stamp'
          ? [523.25, 659.25, 783.99]
          : cue === 'react'
            ? [659.25, 587.33]
            : [523.25, 659.25]
    const now = ctx.currentTime
    notes.forEach((frequency, index) => {
      const oscillator = ctx!.createOscillator()
      const gain = ctx!.createGain()
      oscillator.type = 'sine'
      oscillator.frequency.value = frequency
      gain.gain.setValueAtTime(0.0001, now + index * 0.1)
      gain.gain.exponentialRampToValueAtTime(0.055, now + index * 0.1 + 0.025)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + index * 0.1 + 0.42)
      oscillator.connect(gain)
      gain.connect(master!)
      oscillator.start(now + index * 0.1)
      oscillator.stop(now + index * 0.1 + 0.46)
    })
  }

  return {
    start: async () => {
      await ensure()
      if (ctx?.state === 'suspended') await ctx.resume()
      enabled = true
      applyGain()
    },
    playCue,
    setDucked: (v) => {
      ducked = v
      applyGain()
    },
    setEnabled: (on) => {
      enabled = on
      applyGain()
    },
    get enabled() {
      return enabled
    },
  }
}
